/**
 * Address search (Sprint 103.1, ADR-0120 Rule 3 as amended).
 *
 * Two free services that need no key and no account, called straight from the
 * phone: the Base Adresse Nationale on the Géoplateforme for France, to the
 * house number, and Photon (komoot, OpenStreetMap data) for the rest of the
 * world. Only the typed text is sent: never the position, the camera or the
 * motion readings. The page's Content Security Policy allows these two hosts
 * and no other.
 */

import type { Language } from '../i18n/i18n.ts';

export const BAN_SEARCH = 'https://data.geopf.fr/geocodage/search';
export const PHOTON_SEARCH = 'https://photon.komoot.io/api/';
/** Nothing is sent before this many letters. */
export const MIN_QUERY_LENGTH = 3;
const PER_SERVICE = 5;
const MAX_HITS = 6;
/** Below this, BAN's best answer is a loose match and the world's may be meant. */
const BAN_CONFIDENT = 0.5;

export interface PlaceHit {
  readonly name: string;
  readonly detail: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly source: 'BAN' | 'OSM';
  /** An IANA zone, when the country tells it without a lookup. */
  readonly zone?: string;
  readonly countryCode?: string;
  /** BAN's relevance, 0 to 1. */
  readonly score?: number;
}

export function banUrl(query: string): string {
  return `${BAN_SEARCH}?q=${encodeURIComponent(query)}&limit=${PER_SERVICE}&autocomplete=1&index=address`;
}

/** Photon speaks French, English, German and its own default. */
export function photonUrl(query: string, language: Language): string {
  return `${PHOTON_SEARCH}?q=${encodeURIComponent(query)}&limit=${PER_SERVICE}&lang=${language}`;
}

interface Feature {
  readonly geometry?: { readonly coordinates?: readonly number[] };
  readonly properties?: Record<string, unknown>;
}

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;

function coordinates(feature: Feature): [number, number] | undefined {
  const c = feature.geometry?.coordinates;
  const lon = c?.[0];
  const lat = c?.[1];
  return typeof lon === 'number' &&
    typeof lat === 'number' &&
    Number.isFinite(lon) &&
    Number.isFinite(lat)
    ? [lat, lon]
    : undefined;
}

/** France's overseas departments by postcode; everywhere else in BAN is Paris's time. */
const OVERSEAS_ZONES: Readonly<Record<string, string>> = {
  '971': 'America/Guadeloupe',
  '972': 'America/Martinique',
  '973': 'America/Cayenne',
  '974': 'Indian/Reunion',
  '975': 'America/Miquelon',
  '976': 'Indian/Mayotte'
};

function frenchZone(postcode: string | undefined): string | undefined {
  if (postcode === undefined) return 'Europe/Paris';
  if (!postcode.startsWith('97') && !postcode.startsWith('98')) return 'Europe/Paris';
  return OVERSEAS_ZONES[postcode.slice(0, 3)];
}

export function parseBan(body: unknown): PlaceHit[] {
  const features = (body as { features?: readonly Feature[] } | null)?.features ?? [];
  return features.flatMap((feature): PlaceHit[] => {
    const at = coordinates(feature);
    const p = feature.properties ?? {};
    const name = text(p['name']) ?? text(p['label']);
    if (at === undefined || name === undefined) return [];
    const postcode = text(p['postcode']);
    const city = text(p['city']);
    const detail =
      p['type'] === 'municipality'
        ? (text(p['context']) ?? '')
        : [postcode, city].filter((v) => v !== undefined).join(' ');
    const zone = frenchZone(postcode);
    const score = typeof p['score'] === 'number' ? p['score'] : undefined;
    return [
      {
        name,
        detail,
        latitude: at[0],
        longitude: at[1],
        source: 'BAN',
        countryCode: 'FR',
        ...(zone === undefined ? {} : { zone }),
        ...(score === undefined ? {} : { score })
      }
    ];
  });
}

/**
 * Countries with one clock, so a found place's time follows its summer time
 * rather than being read from its longitude. Countries with several zones, or
 * with islands on another clock, are left out and keep the longitude's time.
 */
const COUNTRY_ZONES: Readonly<Record<string, string>> = {
  AD: 'Europe/Andorra',
  AT: 'Europe/Vienna',
  BE: 'Europe/Brussels',
  CH: 'Europe/Zurich',
  CZ: 'Europe/Prague',
  DE: 'Europe/Berlin',
  DK: 'Europe/Copenhagen',
  FI: 'Europe/Helsinki',
  GB: 'Europe/London',
  GR: 'Europe/Athens',
  HR: 'Europe/Zagreb',
  HU: 'Europe/Budapest',
  IE: 'Europe/Dublin',
  IT: 'Europe/Rome',
  LU: 'Europe/Luxembourg',
  MA: 'Africa/Casablanca',
  MC: 'Europe/Monaco',
  NL: 'Europe/Amsterdam',
  NO: 'Europe/Oslo',
  PL: 'Europe/Warsaw',
  SE: 'Europe/Stockholm',
  TN: 'Africa/Tunis',
  JP: 'Asia/Tokyo',
  IN: 'Asia/Kolkata'
};

function worldZone(
  countryCode: string | undefined,
  latitude: number,
  longitude: number
): string | undefined {
  if (countryCode === undefined) return undefined;
  // Mainland France only: OpenStreetMap tags the overseas departments FR too.
  if (countryCode === 'FR') {
    return latitude > 41 && latitude < 52 && longitude > -6 && longitude < 10
      ? 'Europe/Paris'
      : undefined;
  }
  return COUNTRY_ZONES[countryCode];
}

export function parsePhoton(body: unknown): PlaceHit[] {
  const features = (body as { features?: readonly Feature[] } | null)?.features ?? [];
  return features.flatMap((feature): PlaceHit[] => {
    const at = coordinates(feature);
    const p = feature.properties ?? {};
    const street = [text(p['housenumber']), text(p['street'])]
      .filter((v) => v !== undefined)
      .join(' ');
    const name = text(p['name']) ?? (street === '' ? undefined : street);
    if (at === undefined || name === undefined) return [];
    const town = [text(p['postcode']), text(p['city'])].filter((v) => v !== undefined).join(' ');
    const detail = [town === '' ? undefined : town, text(p['state']), text(p['country'])]
      .filter((v) => v !== undefined && v !== name)
      .join(', ');
    const code = text(p['countrycode'])?.toUpperCase();
    const zone = worldZone(code, at[0], at[1]);
    return [
      {
        name,
        detail,
        latitude: at[0],
        longitude: at[1],
        source: 'OSM',
        ...(code === undefined ? {} : { countryCode: code }),
        ...(zone === undefined ? {} : { zone })
      }
    ];
  });
}

/**
 * One list from both: France's answers first when BAN is sure of itself, with
 * Photon's French answers then dropped as the same places told less precisely;
 * otherwise the world's first. The same point is never listed twice.
 */
export function mergeHits(ban: readonly PlaceHit[], photon: readonly PlaceHit[]): PlaceHit[] {
  const confident = (ban[0]?.score ?? 0) >= BAN_CONFIDENT;
  const world = confident ? photon.filter((hit) => hit.countryCode !== 'FR') : photon;
  const ordered = confident ? [...ban, ...world] : [...world, ...ban];
  const seen = new Set<string>();
  return ordered
    .filter((hit) => {
      const key = `${hit.latitude.toFixed(3)},${hit.longitude.toFixed(3)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, MAX_HITS);
}

export interface SearchResult {
  readonly hits: readonly PlaceHit[];
  /** True when neither service answered: offline, or both refused. */
  readonly failed: boolean;
}

async function ask(url: string, signal: AbortSignal, fetcher: typeof fetch): Promise<unknown> {
  const response = await fetcher(url, {
    signal,
    // Nothing about the page or the person goes with the text.
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    headers: { Accept: 'application/json' }
  });
  if (!response.ok) throw new Error(`status ${response.status}`);
  return response.json();
}

export async function searchPlaces(
  query: string,
  language: Language,
  signal: AbortSignal,
  fetcher: typeof fetch = globalThis.fetch.bind(globalThis)
): Promise<SearchResult> {
  const q = query.trim();
  if (q.length < MIN_QUERY_LENGTH) return { hits: [], failed: false };
  const [ban, photon] = await Promise.allSettled([
    ask(banUrl(q), signal, fetcher).then(parseBan),
    ask(photonUrl(q, language), signal, fetcher).then(parsePhoton)
  ]);
  const hits = mergeHits(
    ban.status === 'fulfilled' ? ban.value : [],
    photon.status === 'fulfilled' ? photon.value : []
  );
  return { hits, failed: ban.status === 'rejected' && photon.status === 'rejected' };
}

/** Two numbers typed in the box, `48.8566, 2.3522` or `48,8566 2,3522`: a place as it stands. */
export function parseCoordinates(
  input: string
): { latitude: number; longitude: number } | undefined {
  const s = input.trim();
  // A comma with spaces around it, a semicolon or spaces separate; a comma between digits is a decimal one.
  let parts = s.includes(';') || /\s,|,\s/.test(s) ? s.split(/\s*[;,]\s*/) : s.split(/\s+/);
  // `48.85,2.35`: with dots for decimals, the one comma separates.
  if (parts.length === 1 && s.includes('.') && s.split(',').length === 2) parts = s.split(',');
  if (parts.length !== 2) return undefined;
  const [lat, lon] = parts.map((part) =>
    /^[-+]?\d+([.,]\d+)?$/.test(part) ? Number(part.replace(',', '.')) : NaN
  );
  if (lat === undefined || lon === undefined || !Number.isFinite(lat) || !Number.isFinite(lon))
    return undefined;
  return Math.abs(lat) <= 90 && Math.abs(lon) <= 180
    ? { latitude: lat, longitude: lon }
    : undefined;
}
